<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useDateTime } from '@ai/ui-components'
import { useLanguages } from '@/composables/translations/useLanguages'
import { capitalize } from '@/utils/String'
import { useLiveAgents } from '@/composables/livesupervision/useLiveVirtualAgents'
import { useAgentStatusMeta } from '@/composables/livesupervision/useAgentStatusMeta'
import { useBadgeContent } from '@/composables/livesupervision/useBadgeContent'
import { useAgentsRealtime } from '@/composables/realtime/useAgentsRealtime.ts'
import { useLivekit, useLivekitCore } from '@/composables/livekit'
import { useLivekitRoomStatusPolling } from '@/composables/livekit/useLivekitRoomStatusPolling'
import ActiveCallModal from '@/components/BusinessLogic/Modal/ActiveCallModal.vue'
import ResetFiltersButton from '@/components/ResetFiltersButton.vue'

import type { ActiveCall } from 'shared/src'

const { t, locale } = useI18n()
const { formatRelativeDate } = useDateTime()
const { languageLabel } = useLanguages()
const {
  agentsToReorder,
  virtualAgentsFiltersOptions,
  agentsList,
  selected,
  isAllSelected,
  isAllLanguagesSelected,
  isAllStatusesSelected,
  isAllAgentsStatusesSelected,
  onOrderSaved,
  hasActiveFilters,
  resetFilters,
} = useLiveAgents(locale.value, t)
const { getBadgeColor, getBadgeIcon } = useAgentStatusMeta()
const { getBadgeContent } = useBadgeContent()
const { isLoading } = useAgentsRealtime()
const { disconnectedRooms } = useLivekitCore()
const activeRoomNames = computed(() =>
  agentsList.value.flatMap((agent) => agent.activeCalls.map((call) => call.roomName)),
)

useLivekitRoomStatusPolling(activeRoomNames)

const hiddenRoomNames = ref<Set<string>>(new Set())
const hideDisconnectedCalls = () => {
  hiddenRoomNames.value = new Set([...hiddenRoomNames.value, ...disconnectedRooms.value])
}

const visibleAgentsList = computed(() =>
  agentsList.value
    .map((agent) => {
      const activeCalls = agent.activeCalls.filter(
        (call) => !hiddenRoomNames.value.has(call.roomName),
      )

      return {
        ...agent,
        activeCalls,
        activeCallsCount: activeCalls.length,
      }
    })
    .filter((agent) => agent.activeCalls.length > 0),
)

const { audio } = useLivekit({
  audio: true,
  context: false,
})
const onClick = (agentId: string) => audio?.toggleListening(agentId)

const listeningAgentId = computed(() => audio?.currentListeningId.value ?? null)
const isCustomizeLayoutDisabled = computed(() => agentsToReorder.value.length < 2)

// Modal management
const modalRef = ref<InstanceType<typeof ActiveCallModal>>()
const selectedCall = ref<ActiveCall | null>(null)
const selectedAgentId = ref<string>('')
const selectedAgentCalls = ref<ActiveCall[]>([])

const openModal = async (item: ActiveCall, agentId: string, calls: ActiveCall[]) => {
  selectedCall.value = item
  selectedAgentId.value = agentId
  selectedAgentCalls.value = calls
  await nextTick()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  modalRef.value?.openPanel(item.roomName)
}

const isTruncatedById = ref<Record<string, boolean>>({})
const checkTruncated = (agentId: string, event: MouseEvent) => {
  const el = event.currentTarget as HTMLElement
  isTruncatedById.value[agentId] = el.scrollWidth > el.clientWidth
}
const uncheckTruncated = (agentId: string) => {
  isTruncatedById.value[agentId] = false
}

const colonSpacer = computed(() => (locale.value.startsWith('fr') ? '\u00A0:' : ':'))
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex flex-col space-y-6 select-none sticky top-0 z-20 px-10 pb-10 pt-10">
      <h1 class="typography-heading-1">{{ t('navigation.live_supervision') }}</h1>
      <LivekitAudioPlayer v-if="audio" :audio="audio" />

      <div class="flex flex-wrap justify-between items-center gap-y-3">
        <div role="search" :aria-label="t('navigation.live_supervision')" class="flex space-x-m">
          <ODropdown
            v-model="selected.agents"
            :options="virtualAgentsFiltersOptions.agents"
            medium
            :searchable="virtualAgentsFiltersOptions.agents.length >= 6"
            multi-select
            :filter-title="isAllSelected ? t('all_agents') : t('agent')"
            filter
          />

          <ODropdown
            v-model="selected.filters.agentsStatuses"
            :options="virtualAgentsFiltersOptions.agentsStatuses"
            medium
            :searchable="virtualAgentsFiltersOptions.agentsStatuses.length >= 6"
            multi-select
            :filter-title="
              isAllAgentsStatusesSelected ? t('all_agent_statuses') : t('agent_status')
            "
            filter
          />

          <ODropdown
            v-model="selected.filters.statuses"
            :options="virtualAgentsFiltersOptions.statuses"
            medium
            :searchable="virtualAgentsFiltersOptions.statuses.length >= 6"
            multi-select
            :filter-title="isAllStatusesSelected ? t('all_call_statuses') : t('call_status')"
            filter
          />

          <ODropdown
            v-model="selected.filters.languages"
            :options="virtualAgentsFiltersOptions.languages"
            medium
            :searchable="virtualAgentsFiltersOptions.languages.length >= 6"
            multi-select
            :filter-title="isAllLanguagesSelected ? t('all_call_languages') : t('call_language')"
            filter
          />

          <ResetFiltersButton :visible="hasActiveFilters" @reset="resetFilters" />
        </div>

        <div class="flex items-center space-x-3">
          <ActivateRealtimeVa @refresh="hideDisconnectedCalls" />
          <VaDragModal @order-saved="onOrderSaved">
            <template #default="{ handleClick }">
              <OListActions
                small
                placement="bottom-end"
                data-testid="live-supervision-actions"
                :actions="[
                  {
                    label: t('customize_layout'),
                    value: 'customize-layout',
                    disabled: isCustomizeLayoutDisabled,
                    handler: () => handleClick(agentsToReorder),
                  },
                ]"
              />
            </template>
          </VaDragModal>
        </div>
      </div>
    </div>
    <div class="flex-1 overflow-y-auto px-10 pb-10">
      <template v-if="visibleAgentsList.length">
        <OCardAdvanced
          v-for="agent in visibleAgentsList"
          :key="agent.id"
          class="w-full flex flex-col space-y-6"
          elevation="S"
        >
          <div class="mb-6 col-span-2">
            <div class="flex justify-between items-center gap-4">
              <OToolTip
                :content="agent.name"
                class="typography-heading-2 text-primary-brand truncate max-w-full"
                :enabled="!!isTruncatedById[agent.id]"
              >
                <h2
                  class="truncate"
                  @mouseenter="checkTruncated(agent.id, $event)"
                  @mouseleave="uncheckTruncated(agent.id)"
                >
                  {{ agent.name }}
                </h2>
              </OToolTip>

              <OBadge
                class="!bg-white shrink-0"
                variant="secondary"
                :custom-icon-color="getBadgeColor(agent)"
                :leading-icon="getBadgeIcon(agent)"
                :content="getBadgeContent(agent)"
              />
            </div>
            <div class="flex space-x-l mt-2 text-neutral-1">
              <div class="flex items-center space-x-1 whitespace-nowrap">
                <UiIcon name="hours" variant="outline" class="mr-s" />
                <span class="typography-body-1">{{ t('uptime') }}{{ colonSpacer }} </span>
                <span class="typography-body-1-strong">
                  {{ formatRelativeDate({ value: agent.uptimeStartedAt, fallback: '-' }).display }}
                </span>
              </div>
              <ODivider orientation="vertical" class="py-0.5 px-2" />

              <div class="flex items-center space-x-1 whitespace-nowrap">
                <UiIcon name="phone" variant="outline" class="mr-s" />
                <span class="typography-body-1">{{ t('active_calls') }}{{ colonSpacer }}</span>
                <span class="typography-body-1-strong">{{ agent.activeCallsCount }}</span>
              </div>
              <ODivider orientation="vertical" class="py-0.5 px-2" />

              <div class="flex items-center space-x-1 min-w-0">
                <UiIcon name="microphone" variant="outline" class="mr-s" />
                <span class="typography-body-1">
                  {{ agent.languages.length >= 2 ? t('languages') : t('language')
                  }}{{ colonSpacer }}
                </span>
                <span
                  class="typography-body-1-strong truncate"
                  :title="
                    agent.languages
                      .map((lang) => capitalize(languageLabel(locale, lang)))
                      .join(', ')
                  "
                >
                  {{
                    agent.languages
                      .map((lang) => capitalize(languageLabel(locale, lang)))
                      .join(', ')
                  }}
                </span>
              </div>
            </div>
          </div>

          <div class="col-span-2">
            <ActiveVaCalls
              :calls="agent.activeCalls"
              :agent-id="agent.id"
              :listening-id="listeningAgentId"
              @on-click-listen="onClick"
              @on-click-details="(item) => openModal(item, agent.id, agent.activeCalls)"
              class="max-h-72"
            />
          </div>
        </OCardAdvanced>
      </template>
      <div v-else-if="isLoading" class="flex flex-1 flex-col items-center justify-center py-16">
        <OSpinner size="XL" />
      </div>
      <div v-else class="flex flex-1 flex-col items-center justify-center py-16">
        <OState type="empty" variant="1" />
      </div>
    </div>

    <ActiveCallModal
      v-if="selectedCall"
      ref="modalRef"
      :item="selectedCall"
      :calls="selectedAgentCalls"
      :agentId="selectedAgentId"
    />
  </div>
</template>
